import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { useSessionRenewals } from "./session-ended.ts";

// The session-scoped cache rules both pages share (the tenant app and the console website).

/** A 401 on a live session (a token rotated elsewhere): fetch the session's current token. */
export function useRefreshOnRenewal() {
  const queryClient = useQueryClient();
  const renewals = useSessionRenewals();
  useEffect(() => {
    if (renewals > 0) {
      void queryClient.invalidateQueries({ queryKey: ["session"] });
    }
  }, [renewals, queryClient]);
}

/**
 * Another account signed in (another tab, say): drop every cached answer of the previous one
 * before anything renders from it, and run `forget` for the page's own state. The session
 * query itself is kept.
 */
export function useForgetOnIdentityChange(identity: string | null, forget?: () => void) {
  const queryClient = useQueryClient();
  const previous = useRef(identity);
  // During render, not in an effect: the remounted views must never read the old answers.
  // Removing queries is idempotent, so a repeated render does no harm.
  if (previous.current !== identity) {
    previous.current = identity;
    forget?.();
    queryClient.removeQueries({ predicate: (q) => q.queryKey[0] !== "session" });
  }
}
